import { ClbDrawer, ClbLoading } from '@celebration/react';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import ILoadHistoryResponse, {
    ILoadHistoryData,
    ILoadHistoryEventResponse,
} from '../../interfaces/ILoadHistoryResponse';
import './load-history-drawer.scss';
import SimpleHistoryItem from './components/simple-history-item/simple-history-item';
import HistoryItemWithCarriers from './components/history-item-with-carriers/history-item-with-carriers';
import { LoadChangedAction } from '../../enums/load-changed-action.enum';
import HistoryItemConfirmed from './components/history-item-confirmed/history-item-confirmed';
import LoadHistoryService from './services/load-history-drawer.service';
import ILoadCardInfo from '../../../../../common/interfaces/loads/ILoadCardInfo';
import HistoryItemUnsuspended from './components/history-item-unsuspended/history-item-unsuspended';
import HistoryItemSentToPool from './components/history-item-sent-to-pool/history-item-sent-to-pool';
import HistoryItemVisibilityChanged from './components/history-item-visibility-changed/history-item-visibility-changed';
import HistoryItemConfirmationTermsAccepted from './components/history-item-confirmation-terms-accepted/history-item-confirmation-terms-accepted';

function LoadHistoryDrawer({
    loadData,
    open,
    setOpenHistoryDrawer,
    onUnsuspended,
}: Readonly<{
    loadData: ILoadCardInfo;
    open: boolean;
    setOpenHistoryDrawer: any;
    onUnsuspended?: () => void;
}>) {
    const { t } = useTranslation();
    const isFirstFetch = useRef(true);
    const hasUnsuspendedEvent = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadHistory, setLoadHistory] = useState<ILoadHistoryData>();
    const [drawerState, setDrawerState] = useState<
        'closed' | 'opening' | 'open'
    >('closed');

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        LoadHistoryService.getAllLoadHistoryPaginated(loadData.idLoad)
            .then((resp: ILoadHistoryResponse) => {
                const rawHistory =
                    Array.isArray(resp?.data) && resp.data.length > 0
                        ? resp.data[0]
                        : undefined;

                const normalizedHistory = rawHistory
                    ? ({
                          ...rawHistory,
                          events: Array.isArray(rawHistory.events)
                              ? rawHistory.events
                              : [],
                      } as ILoadHistoryData)
                    : ({ events: [] } as ILoadHistoryData);

                setLoadHistory(normalizedHistory);
            })
            .catch(() => {
                setLoadHistory({
                    events: [],
                } as ILoadHistoryData);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [loadData.idLoad]);

    useEffect(() => {
        setDrawerState(open ? 'opening' : 'closed');
    }, [open]);

    useEffect(() => {
        if (drawerState === 'opening') {
            if (isFirstFetch.current) {
                isFirstFetch.current = false;
                fetchData();
            }
            setDrawerState('open');
        }
    }, [drawerState, fetchData]);

    useEffect(() => {
        if (drawerState === 'closed') {
            isFirstFetch.current = true;
            hasUnsuspendedEvent.current = false;
            setLoadHistory(undefined);
        }
    }, [drawerState]);

    const rawEvents = Array.isArray(loadHistory?.events)
        ? (loadHistory?.events ?? [])
        : [];

    const filteredEvents = rawEvents;

    const eventPriority: Partial<Record<LoadChangedAction, number>> = {
        [LoadChangedAction.ReOffer]: 1,
        [LoadChangedAction.Download]: 2,
        [LoadChangedAction.Ready]: 3,
        [LoadChangedAction.SentToPool]: 4,
        [LoadChangedAction.ConfirmationPool]: 5,
        [LoadChangedAction.ConfirmationTermsAccepted]: 6,
        [LoadChangedAction.Confirmed]: 7,
    };

    const getEventPriority = (eventType: LoadChangedAction): number =>
        eventPriority[eventType] ?? 0;

    const truncateToSecond = (date: Date): number => {
        return Math.floor(new Date(date).getTime() / 1000);
    };

    const events = filteredEvents
        .map((event, originalIndex) => ({ event, originalIndex }))
        .sort((a, b) => {
            const dateA = truncateToSecond(a.event.eventDate);
            const dateB = truncateToSecond(b.event.eventDate);

            if (dateA !== dateB) {
                return dateA - dateB;
            }

            const priorityDiff =
                getEventPriority(a.event.eventType) -
                getEventPriority(b.event.eventType);

            return priorityDiff === 0
                ? a.originalIndex - b.originalIndex
                : priorityDiff;
        })
        .map(({ event }) => event);

    const shouldRenderLoadHistoryEvents = !isLoading && events.length > 0;

    useEffect(() => {
        if (events.length > 0 && !hasUnsuspendedEvent.current) {
            const hasUnsuspended = events.some(
                ev => ev.eventType === LoadChangedAction.Unsuspended,
            );
            if (hasUnsuspended) {
                hasUnsuspendedEvent.current = true;
                onUnsuspended?.();
            }
        }
    }, [events, onUnsuspended]);

    const renderHistoryItem = (event: ILoadHistoryEventResponse) => {
        const key = `${event.eventDate}-${event.eventType}`;

        switch (event.eventType) {
            case LoadChangedAction.Confirmed:
            case LoadChangedAction.ConfirmationPool:
            case LoadChangedAction.TenderAccepted:
                return <HistoryItemConfirmed key={key} history={event} />;
            case LoadChangedAction.WaitingAcceptance:
            case LoadChangedAction.WaitingConfirmation:
                return <HistoryItemWithCarriers key={key} history={event} />;
            case LoadChangedAction.Unsuspended:
                return <HistoryItemUnsuspended key={key} history={event} />;
            case LoadChangedAction.SentToPool:
                return <HistoryItemSentToPool key={key} history={event} />;
            case LoadChangedAction.VisibilityChanged:
                return (
                    <HistoryItemVisibilityChanged key={key} history={event} />
                );
            case LoadChangedAction.ConfirmationTermsAccepted:
                return (
                    <HistoryItemConfirmationTermsAccepted
                        key={key}
                        history={event}
                    />
                );
            default:
                return <SimpleHistoryItem key={key} history={event} />;
        }
    };

    return (
        <div className="load-history-drawer-wrapper">
            <ClbDrawer
                title={t('LoadHistory.Title')}
                open={open}
                size="lg"
                onClose={() => setOpenHistoryDrawer(false)}
            >
                <div className="load-history">
                <div className=" load-history-line">
                    <span data-testid="load-history-title">
                        {t('LoadHistory.LoadLabel', {
                            loadId: loadData.idLoad,
                        })}
                    </span>
                </div>
                {isLoading && <ClbLoading size="sm" />}

                <div>
                    {shouldRenderLoadHistoryEvents &&
                        events.map((history: ILoadHistoryEventResponse) => {
                            return renderHistoryItem(history);
                        })}
                </div>
            </div>
        </ClbDrawer>
        </div>
    );
}

export default LoadHistoryDrawer;
